import { InterfaceModes } from '@unifyapps/carbon/no-code/stores/InterfaceStore/types';
import { UAEntityTypes } from '@unifyapps/defs/types/entities';
import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import { InterfacePageEntityComponentType } from '@unifyapps/defs/types/page';
import {
  getSearchInterfacePageEmbeddedEntitiesQueryKey,
  useFindEntityById,
  useSearchInterfacePageEmbeddedEntities,
} from '@unifyapps/network/generated/entity-rest-api/entity-rest-api';
import { useMemo, useRef } from 'react';
import { debugSSR } from '../../../../../../consts/debug';
import { NO_INTERFACE_RECORD_ID } from '../../../../../const';
import { getDummyPage, NO_INTERFACE_RECORD } from '../../../../../const/dummy';
import InterfaceHelper from '../../../../../stores/InterfaceStore/helper/InterfaceHelper';
import {
  EMBEDDED_ENTITIES_EXCLUDE_FIELDS,
  STANDARD_ALLOWED_ENTITY_TYPES,
} from '../../../../../utils/interfaceQueryFilters';
import {
  getAllPagesFromEmbedded,
  normalizeEmbeddedDependents,
} from '../../../../../utils/normalizeEmbeddedDependents';
import { buildInterfacePageEmbeddedRequest } from '../../../buildInterfacePageEmbeddedRequest';
import type { UseFetchResourcesHookType } from '../../../types';
import { useFetchCustomBlockSnapshotsForPages } from '../../../useFetchCustomBlocks/useFetchCustomBlockSnapshotsForPages';
import { useEvictQueriesOnMount } from '../../invalidation/useEvictQueriesOnMount';
import { useFetchInterfacePageNamespacesForPages } from '../useFetchInterfaceNamespaces/useFetchInterfacePageNamespacesForPages';

type PreviousResolutionCache = {
  interfaceId: string;
  slug: string;
  resolvedPageId: string;
} | null;

const useFetchInterfaceResources: UseFetchResourcesHookType = ({
  interfaceId,
  interfaceComponent,
  mode,
  locale,
  fetchOnMount,
}) => {
  const isNoInterface = interfaceId === NO_INTERFACE_RECORD_ID;

  const {
    data: interfaceRecordFromApi,
    isLoading: isLoadingInterfaceRecord,
    error: errorInterfaceRecord,
    // TODO: need backend API support to exclude fields (e.g. outSchema) from findEntityById to reduce payload size in non-builder mode
  } = useFindEntityById<InterfaceEntity>(UAEntityTypes.Interface, interfaceId ?? '', {
    query: {
      enabled: Boolean(interfaceId) && !isNoInterface,
      retry: 3,
      staleTime: Infinity,
    },
  });

  const isBuilder = mode === InterfaceModes.BUILDER;
  const interfaceRecord = isNoInterface ? NO_INTERFACE_RECORD : interfaceRecordFromApi;

  const isAIBuilding = InterfaceHelper.isAIBuilding(interfaceComponent);
  // builder-only fallback: keeps the page rendering when a mid-session slug change leaves
  // the cached slug map briefly inconsistent with the URL (rename → map invalidates →
  // URL hasn't updated yet, or vice versa).
  const previousResolutionRef = useRef<PreviousResolutionCache>(null);

  // In non-builder mode, exclude diffs and outputSchema to reduce payload size.
  const excludeFields = isBuilder ? undefined : EMBEDDED_ENTITIES_EXCLUDE_FIELDS;

  // Resolve the page request through the helper. Builder mode threads the most recent
  // successful resolution in via `defaultPageId` so it preempts the slug-query fallback
  // during the one-frame window when the slug map is stale.
  const { request, enabled } = useMemo(() => {
    const isPage = interfaceComponent.type === InterfacePageEntityComponentType.PAGE;

    let refCachedPageId: string | undefined;
    if (isBuilder && isPage) {
      const ref = previousResolutionRef.current;
      if (ref?.interfaceId === interfaceId && ref.slug === interfaceComponent.slug) {
        refCachedPageId = ref.resolvedPageId;
      }
    }

    const result = buildInterfacePageEmbeddedRequest({
      interfaceId,
      interfaceComponent,
      interfaceRecord,
      defaultPageId: refCachedPageId,
      excludeFields,
      allowedEntityTypes: STANDARD_ALLOWED_ENTITY_TYPES,
    });

    // Snapshot any successful resolution so the next render can recover from a stale slug map.
    if (isBuilder && isPage && result.resolvedPageId) {
      previousResolutionRef.current = {
        interfaceId,
        slug: interfaceComponent.slug,
        resolvedPageId: result.resolvedPageId,
      };
    }

    return { request: result.request, enabled: result.enabled };
  }, [interfaceId, interfaceComponent, interfaceRecord, isBuilder, excludeFields]);

  // collaboration freshness: when the caller opts in via fetchOnMount, evict
  // this query's cache entry synchronously on the first render so the
  // useSearchInterfacePageEmbeddedEntities observer below reads an empty cache,
  // enters isPending → fires a fresh fetch — yielding a real loader on every mount.
  //
  // The eviction key is the full request-specific key (not the endpoint
  // prefix), so sibling observers using the same endpoint with different
  // requests (e.g. modules embedded in the same app) keep their cache and do
  // NOT go into loading.
  //
  // Once the fetch settles and isLoading flips to false, external
  // queryClient.invalidateQueries calls fire isFetching: true but isLoading
  // remains false — so the loader does not show and the page tree does not
  // unmount mid-session.
  useEvictQueriesOnMount(
    [getSearchInterfacePageEmbeddedEntitiesQueryKey(request)],
    fetchOnMount === true,
  );

  // IMPORTANT: getModuleFromStore (apps/platform/.../useGetNamespaceTranslations/utils/
  // getModuleFromStore.ts) reconstructs the cache key for this exact
  // useSearchInterfacePageEmbeddedEntities call to read the module page out of the react-query
  // cache during interface save. If you change the request shape (entityId, excludeFields) or
  // swap the underlying hook here, update getModuleFromStore in lockstep — otherwise the cache
  // lookup misses and module input-schema localization labels stop being generated.
  const {
    data: embeddedResp,
    isLoading: isLoadingPage,
    error: errorPage,
  } = useSearchInterfacePageEmbeddedEntities(request, {
    query: {
      enabled: enabled && !isAIBuilding,
      retry: 3,
      staleTime: Infinity,
    },
  });

  const { interfacePage, dataSources, embeddedPages, embeddedInterfaceRecords } = useMemo(
    () => normalizeEmbeddedDependents(embeddedResp),
    [embeddedResp],
  );

  // Fetch namespaces and custom blocks for the root page plus every nested page returned by
  // embedded-dependents. We compose the list once and pass it to the multi-page hooks so each
  // (page, locale) tuple is cached under the same query key the single-page hook would use.
  const allPages = useMemo(
    () => getAllPagesFromEmbedded(interfacePage, embeddedPages),
    [interfacePage, embeddedPages],
  );

  const {
    namespaces,
    isLoading: isLoadingInterfaceNamespaces,
    error: errorInterfaceNamespaces,
  } = useFetchInterfacePageNamespacesForPages({
    interfaceId,
    pages: allPages,
    interfaceRecord,
    mode,
    locale,
  });

  const interfacePageNamespace = interfacePage?.id ? namespaces?.[interfacePage.id] : undefined;

  const { data: customBlocksSnapshots, isLoading: isLoadingCustomBlocksSnapshots } =
    useFetchCustomBlockSnapshotsForPages(allPages);

  const _isLoading =
    isLoadingInterfaceRecord ||
    isLoadingPage ||
    isLoadingInterfaceNamespaces ||
    isLoadingCustomBlocksSnapshots;

  const _error = errorInterfaceRecord || errorPage || errorInterfaceNamespaces;

  debugSSR(
    'useFetchInterfaceResources',
    {
      isLoading: _isLoading,
      isLoadingInterfaceRecord,
      isLoadingPage,
      isLoadingInterfaceNamespaces,
      isLoadingCustomBlocksSnapshots,
    },
    {
      interfaceId: interfaceRecord?.id,
      pageId: interfacePage?.id,
    },
  );

  // we are returning a dummy page for ai building mode here to avoid interfacePageId getting resovled above and causing errors.
  const finalInterfacePage = isAIBuilding ? getDummyPage({ interfaceId }) : interfacePage;

  return {
    isLoading: _isLoading,
    error: _error,
    data: interfaceRecord,
    dataSources,
    interfacePage: finalInterfacePage,
    interfacePageNamespace,
    namespaces,
    embeddedPages,
    embeddedInterfaceRecords,
    customBlocksSnapshots,
  };
};

export default useFetchInterfaceResources;
